/* eslint-disable */
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { MaterialReactTable } from "material-react-table";

import AutomationTemplateHeader from "components/AutomationComponents/AutomationScheduleTemplatesBody/Header/header";
import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import AutomationTemplateDetailPopup from "components/AutomationComponents/AutomationScheduleTemplatesBody/Popup/AutomationTemplateDetailPopup";
import AutomationTemplateFilterPopup from "components/AutomationComponents/AutomationScheduleTemplatesBody/Popup/AutomationTemplateFilterPopup";

import { automationHeaderLinks } from "helper/AutomationHelper/AutomationMenuLinks";

/*------ import url and method ------*/
import * as url from "helper/UrlHelper";
import { getData } from "utils/Gateway";

/*------ import assets ------*/
import assetImages from "constants/images";

const AutomationScheduleTemplatesBody = () => {
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const { t, i18n } = useTranslation();

  const commonHeaderObject = automationHeaderLinks(t);

  const [isLoading, setIsLoading] = useState(false);
  const [templateList, setTemplateList] = useState([]);

  const [rowSelection, setRowSelection] = useState({});
  const [selectedItemIds, setSelectedItemIds] = useState([]);
  const [reloadData, setReloadData] = useState(false);

  // Detail popup – pass the whole template object (no extra fetch needed)
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showDetailPopup, setShowDetailPopup] = useState(false);

  // Filter
  const [jsonFilterQuery, setJsonFilterQuery] = useState([]);
  const [isFilterReset, setIsFilterReset] = useState(false);

  // Pagination
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [lastPagination, setLastPagination] = useState({ pageIndex: 0, pageSize: 10 });

  // Alert
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // ── Fetch all templates ────────────────────────────────────
  const getAllTemplates = async () => {
    setSelectedItemIds([]);
    setRowSelection({});
    try {
      setIsLoading(true);
      const requestUrl =
        url.API_BASE_URL + url.API_GET_AUTOMATION_TASK_SCHEDULE_TEMPLATES + `?token=${token}`;
      const response = await getData(requestUrl);
      setIsLoading(false);

      if (response.status) {
        setMessageType("success");
        setTemplateList(response.data);
      } else {
        setMessageType("error");
      }
      setAlertMessage(response.message);
    } catch (error) {
      console.error("Error fetching template list:", error);
      setAlertMessage(error.message);
      setMessageType("error");
    }
    setShowAlert(true);
  };

  const resetFilterData = () => {
    setReloadData(true);
    setLastPagination({ pageIndex: 0, pageSize: 10 });
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  const handleViewTemplate = (template) => {
    setSelectedTemplate(template);
    setShowDetailPopup(true);
  };

  const handleCloseDetailPopup = () => {
    setShowDetailPopup(false);
    setSelectedTemplate(null);
  };

  useEffect(() => { getAllTemplates(); }, []);

  useEffect(() => {
    if (reloadData) { getAllTemplates(); setReloadData(false); }
  }, [reloadData]);

  // ── Client-side filter ─────────────────────────────────────
  const filteredTemplateList = useMemo(() => {
    if (!jsonFilterQuery || jsonFilterQuery.length === 0) return templateList;

    return templateList.filter((tpl) =>
      jsonFilterQuery.every(({ field, operator, value }) => {
        const raw = tpl[field];

        // multi-select: value is [{label, value}]
        if (Array.isArray(value) && value[0] !== undefined && typeof value[0] === "object") {
          const vals = value.map((v) => v.value);
          // Handle boolean fields (ispublic)
          const rawNorm = typeof raw === "boolean" ? raw : raw;
          return operator === "not_in" ? !vals.includes(rawNorm) : vals.includes(rawNorm);
        }

        const taskStr = String(raw ?? "").toLowerCase();
        const valStr  = String(value ?? "").toLowerCase();

        switch (operator) {
          case "contains":     return taskStr.includes(valStr);
          case "not_contains": return !taskStr.includes(valStr);
          case "equals":
          case "eq":           return taskStr === valStr;
          case "not_equals":   return taskStr !== valStr;
          case "starts_with":  return taskStr.startsWith(valStr);
          case "ends_with":    return taskStr.endsWith(valStr);
          case "gt":           return raw > value;
          case "lt":           return raw < value;
          case "between":
            return Array.isArray(value) && raw >= value[0] && raw <= value[1];
          default:             return true;
        }
      })
    );
  }, [templateList, jsonFilterQuery]);

  // ── Table columns ──────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <Link
            className="action_btn_mui"
            to="#"
            onClick={(e) => { e.preventDefault(); handleViewTemplate(row.original); }}
          >
            <span className="d-block material-symbols-outlined horz_icon">more_horiz</span>
          </Link>
        ),
        enableColumnActions: false,
        enableColumnDragging: false,
        enableSorting: false,
      },
      {
        accessorKey: "name",
        header: t("Template Name"),
        Cell: ({ row }) => (
          <p>
            <span className="d-block fw-bold">{row.original.name}</span>
            {row.original.description && (
              <span className="text-muted" style={{ fontSize: "0.9em" }}>
                {row.original.description}
              </span>
            )}
          </p>
        ),
      },
      {
        accessorKey: "executiontype",
        header: t("Execution Type"),
        Cell: ({ row }) => {
          const type = row.original.executiontype;
          const isReactive = type === "reactive";
          return (
            <span className={`badge ${isReactive ? "bg-info text-dark" : "bg-warning text-dark"}`}>
              {isReactive ? t("Reactive") : t("Proactive")}
            </span>
          );
        },
      },
      {
        accessorKey: "ispublic",
        header: t("Visibility"),
        Cell: ({ row }) => (
          <span className={`badge ${row.original.ispublic ? "bg-success" : "bg-secondary"}`}>
            {row.original.ispublic ? t("Public") : t("Private")}
          </span>
        ),
      },
      {
        accessorKey: "subtaskblueprint",
        header: t("Subtasks"),
        Cell: ({ row }) => {
          const count = row.original.subtaskblueprint?.length || 0;
          return <span className="badge bg-primary">{count} {t("subtasks")}</span>;
        },
      },
      {
        accessorKey: "owner",
        header: t("Owner"),
        Cell: ({ row }) => (
          <div className="d-flex align-items-center gap-2">
            <img
              className="rounded-circle"
              height={36}
              width={36}
              src={
                row.original.owner?.image
                  ? url.SERVER_URL + row.original.owner.image
                  : assetImages.defaultUser
              }
              alt={row.original.owner?.name || "User"}
            />
            <div>
              <div className="fw-semibold" style={{ fontSize: "13px" }}>
                {row.original.owner?.name || t("N/A")} {row.original.owner?.surname || ""}
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                {row.original.owner?.email || ""}
              </div>
            </div>
          </div>
        ),
      },
      {
        accessorKey: "status",
        header: t("Status"),
        Cell: ({ row }) => (
          <span className={`badge ${row.original.status === "active" ? "bg-success" : "bg-danger"}`}>
            {row.original.status || "active"}
          </span>
        ),
      },
      {
        accessorKey: "createdAt",
        header: t("Created Date"),
        Cell: ({ row }) => (
          <div>
            <span>{new Date(row.original.createdAt).toLocaleDateString()}</span>
            <div className="text-muted" style={{ fontSize: "12px" }}>
              {new Date(row.original.createdAt).toLocaleTimeString()}
            </div>
          </div>
        ),
      },
    ],
    [i18n.language]
  );

  const columnOrder = ["#", "mrt-row-select", ...columns.map((c) => c.accessorKey)];

  const refreshRecords = () => resetFilterData();

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN"
  ) {
    return (
      <div id="content_wrapper">
        <section className="survey-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderObject}
            activeOption={t("Scheduled Automation Templates")}
          />

          <div className="container-fluid px-lg-5">
            <AutomationTemplateHeader
              reloadList={() => {
                setJsonFilterQuery([]);
                setIsFilterReset(true);
                refreshRecords();
              }}
            />

            {isLoading ? (
              <div className="placeholder-glow d-flex flex-column gap-4">
                <span className="placeholder placeholder-lg bg-secondary col-12"></span>
                <span className="placeholder placeholder-lg bg-secondary col-8"></span>
                <span className="placeholder placeholder-lg bg-secondary col-4"></span>
              </div>
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={filteredTemplateList}
                  enableGrouping
                  enableRowSelection
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  defaultColumn={{ minSize: 20, maxSize: 200, size: 50 }}
                  muiTableContainerProps={{ sx: { maxHeight: "60vh" } }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>
        </section>

        {/* Template Detail Popup */}
        <AutomationTemplateDetailPopup
          template={selectedTemplate}
          isOpen={showDetailPopup}
          onClose={handleCloseDetailPopup}
        />

        {/* Filter Popup */}
        <AutomationTemplateFilterPopup
          setJsonFilterQuery={setJsonFilterQuery}
          reloadList={() => {
            setJsonFilterQuery([]);
            setIsFilterReset(true);
            refreshRecords();
          }}
          isFilterReset={isFilterReset}
          setIsFilterReset={setIsFilterReset}
        />
      </div>
    );
  } else {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <div className="empty_access text-center">
            <div className="empty_pic mb-4">
              <img src={assetImages.emptyVector} alt="" />
            </div>
            <div className="empty_text">
              <p className="fs-lg text-gray fw-semibold mb-4">
                {t("Sorry....! You don't have privilege to see this content")}
              </p>
            </div>
          </div>
        </section>
      </div>
    );
  }
};

export default AutomationScheduleTemplatesBody;
